import 'date-fns';
import React, {useEffect, useState} from "react";
import {Box, Grid, MenuItem, Typography} from "@material-ui/core";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import {useConsumerContext} from "../../context/ConsumerContext";
import {AVAILABLE_ACTIONS} from "../../utils/constants";
import {MUICardWithControl, MUIDatePicker, MUIInput, MUISelect} from "../../utils/formUtils";
import addressData from "../../data/addressData.json";
import add from "date-fns/add/index.js"
import {WizardNav} from "../../utils/fmapUtils";
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import TodayIcon from '@mui/icons-material/Today';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import ShoppingBasketRoundedIcon from '@mui/icons-material/ShoppingBasketRounded';
import BedroomParentRoundedIcon from '@mui/icons-material/BedroomParentRounded';

const validationSchema = yup.object().shape({
    insuranceReason: yup.string().required('Please complete all required fields').nullable().notOneOf(['none'], 'Please complete all required fields'),
    coverageStartDate: yup.date().required("Date required.").min(add(new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()), {days: 1}), "No Prior Date Allowed.").max(add(new Date().setMonth(new Date().getMonth()+6),{days:1}),"Coverage need by date should be within 6 months").nullable().typeError(),
    coverageAmount: yup.number().typeError("Enter valid number.").required("Value amount required.").positive("Value amount required.").integer().min(1, 'Value amount required.'),
    currentInsurer: yup.string().when('insuranceReason', {
        is: (insuranceReason) => ['SHOPPING_INSURANCE', 'BEING_CANCELLED'].includes(insuranceReason),
        then: yup.string().required('Company required.').nullable(),
        otherwise: yup.string().notRequired().nullable()
    }),
    // propertyOccupiedBy: yup.string().required('Please complete all required fields').nullable(),
    propertyUsage: yup.string().required('Please complete all required fields').nullable().notOneOf(['none'], 'Please complete all required fields'),
})
export default function PropertyRequirement() {
    const {handleSubmit, control, trigger, formState: {errors}} = useForm({resolver: yupResolver(validationSchema)});
    // context from parent
    const {consumerData, dispatch} = useConsumerContext();

    // loading static data when component loads
    useEffect(() => {
        // Anything in here is fired on component mount.
        // fetching static data
        setInsuranceReasonList(addressData.insuranceReason);
        setOccupiedByList(addressData.propertyOccupiedBy);
        setUsageList(addressData.propertyUsage);
    }, []);

    const [insuranceReasonList, setInsuranceReasonList] = useState([]);
    const [occupiedByList, setOccupiedByList] = useState([]);
    const [usageList, setUsageList] = useState([]);

    // sync the date change to state
    const handleCoverageStartDateChange = (coverageDate) => {
        dispatch({
            type: AVAILABLE_ACTIONS.UPDATE_PROPERTY_REQUIREMENT,
            userRequest: {"coverageStartDate": coverageDate}
        });
    };

    // updating the state as the user types...
    function handleFormChange(event) {
        dispatch({
            type: AVAILABLE_ACTIONS.UPDATE_PROPERTY_REQUIREMENT,
            userRequest: {[event.target.name]: event.target.value}
        });
    }

    function conditionalCheckForInsuranceReason(data) {
        if (!['SHOPPING_INSURANCE', 'BEING_CANCELLED'].includes(consumerData?.insuranceReason)) {
            if (data) {
                data["currentInsurer"] = undefined;
            } else {
                dispatch({
                    type: AVAILABLE_ACTIONS.UPDATE_PROPERTY_REQUIREMENT,
                    userRequest: {"currentInsurer": undefined}
                });
            }
        }
    }

    const onSubmit = data => {
        conditionalCheckForInsuranceReason(data);
        dispatch({type: AVAILABLE_ACTIONS.UPDATE_PROPERTY_REQUIREMENT, userRequest: data});
        dispatch({type: AVAILABLE_ACTIONS.MOVE_NEXT, wizardStep: consumerData.wizardStep + 1});
    };

    const handleBackClick = async () => {
        conditionalCheckForInsuranceReason(); // todo if enabling previous check hw it works
        const isValid = await trigger(['coverageStartDate', 'coverageAmount', 'insuranceReason', 'currentInsurer', 'propertyOccupiedBy', 'propertyUsage']);
        if (isValid) {
            dispatch({type: AVAILABLE_ACTIONS.MOVE_NEXT, wizardStep: consumerData.wizardStep - 1});
        }
    }


    return (
        <section>
            <Box m={2} marginBottom={0}>
                <Typography variant="h4" align="left" component="h4" gutterBottom
                            style={{paddingLeft: 24, fontWeight: 600}}>
                    Property Requirement
                </Typography>
            </Box>
            <form
                onSubmit={handleSubmit(onSubmit)}>
                <Box m={3} marginTop={0}>
                    <Grid container alignItems="flex-start" spacing={2} style={{paddingLeft: 8, paddingTop: 8}}>
                        <Grid item xs={12} sm={6} md={4}>
                            <MUICardWithControl label="When do you need coverage on your property?" sx={{mb: 32}}
                                                className="textBottom" style={{marginBottom: 32}} children={
                                <MUIDatePicker name="coverageStartDate" control={control}
                                               minDateMessage=""
                                               dateValue={consumerData?.coverageStartDate}
                                               handleDateChange={handleCoverageStartDateChange}
                                               errors={errors}></MUIDatePicker>
                            } icon={<TodayIcon style={{
                                backgroundColor: '#3f51b5',
                                padding: 16,
                                fontSize: 16,
                                height: 64,
                                width: 64,
                                marginBottom: 16,
                                color: '#ffffff',
                                borderRadius: 20
                            }}/>}/>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <MUICardWithControl
                                label="What is the value of the property excluding land?" children={
                                <MUIInput control={control} name="coverageAmount" inputPrp={{maxLength: 8}}
                                          defaultValue={consumerData?.coverageAmount}
                                          value={consumerData?.coverageAmount}
                                          onInputChange={handleFormChange} errors={errors}></MUIInput>
                            } icon={<AttachMoneyOutlinedIcon style={{
                                backgroundColor: '#3f51b5',
                                padding: 16,
                                fontSize: 16,
                                height: 64,
                                width: 64,
                                marginBottom: 16,
                                color: '#ffffff',
                                borderRadius: 20
                            }}/>}/>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <MUICardWithControl label="Why do you need insurance?" children={
                                <MUISelect id="insuranceReason" control={control} name="insuranceReason" errors={errors}
                                           defaultValue={consumerData?.insuranceReason}
                                           value={consumerData?.insuranceReason} onFormChange={handleFormChange}>
                                    <MenuItem key="none" value="none" disabled>--Select--</MenuItem>
                                    {insuranceReasonList.map(reason => {
                                        return <MenuItem key={reason?.value}
                                                         value={reason?.value}>{reason?.label}</MenuItem>
                                    })}
                                </MUISelect>} icon={<ShoppingBasketRoundedIcon style={{
                                backgroundColor: '#3f51b5',
                                padding: 16,
                                fontSize: 16,
                                height: 64,
                                width: 64,
                                marginBottom: 16,
                                color: '#ffffff',
                                borderRadius: 20
                            }}/>}/>
                        </Grid>
                        {['SHOPPING_INSURANCE', 'BEING_CANCELLED'].includes(consumerData?.insuranceReason) &&
                        <Grid item xs={12} sm={6} md={4}>
                            <MUICardWithControl label="What insurance company currently insures your property?"
                                                children={
                                                    <MUIInput control={control} name="currentInsurer"
                                                              defaultValue={consumerData?.currentInsurer}
                                                              inputPrp={{maxLength: 50}}
                                                              value={consumerData?.currentInsurer}
                                                              onInputChange={handleFormChange}
                                                              errors={errors}></MUIInput>
                                                } icon={<HomeRoundedIcon style={{
                                backgroundColor: '#3f51b5',
                                padding: 16,
                                fontSize: 16,
                                height: 64,
                                width: 64,
                                marginBottom: 16,
                                color: '#ffffff',
                                borderRadius: 20
                            }}/>}/>
                        </Grid>}
                        {/*<Grid item xs={12} sm={6} md={4}>*/}
                        {/*    <MUICardWithControl label="Who occupies the home?" children={*/}
                        {/*        <MUISelect id="propertyOccupiedBy" control={control} name="propertyOccupiedBy"*/}
                        {/*                   errors={errors}*/}
                        {/*                   defaultValue={consumerData?.propertyOccupiedBy}*/}
                        {/*                   value={consumerData?.propertyOccupiedBy} onFormChange={handleFormChange}>*/}
                        {/*            {occupiedByList.map(reason => {*/}
                        {/*                return <MenuItem key={reason?.value}*/}
                        {/*                                 value={reason?.value}>{reason?.label}</MenuItem>*/}
                        {/*            })}*/}
                        {/*        </MUISelect>*/}
                        {/*    } icon={<PersonRoundedIcon style={{*/}
                        {/*        backgroundColor: '#3f51b5',*/}
                        {/*        padding: 16,*/}
                        {/*        fontSize: 16,*/}
                        {/*        height: 64,*/}
                        {/*        width: 64,*/}
                        {/*        marginBottom: 16,*/}
                        {/*        color: '#ffffff',*/}
                        {/*        borderRadius: 20*/}
                        {/*    }}/>}/>*/}
                        {/*</Grid>*/}
                        <Grid item xs={12} sm={6} md={4}>
                            <MUICardWithControl label="How is the property used?" labelPlacement="top" children={
                                <MUISelect id="propertyUsage" control={control} name="propertyUsage" errors={errors}
                                           defaultValue={consumerData?.propertyUsage}
                                           value={consumerData?.propertyUsage} onFormChange={handleFormChange}>
                                    <MenuItem key="none" value="none" disabled>--Select--</MenuItem>
                                    {usageList.map(reason => {
                                        return <MenuItem key={reason?.value}
                                                         value={reason?.value}>{reason?.label}</MenuItem>
                                    })}
                                </MUISelect>
                            } icon={<BedroomParentRoundedIcon style={{
                                backgroundColor: '#3f51b5',
                                padding: 16,
                                fontSize: 16,
                                height: 64,
                                width: 64,
                                marginBottom: 16,
                                color: '#ffffff',
                                borderRadius: 20
                            }}/>}/>
                        </Grid>
                    </Grid>
                </Box>
                <WizardNav handleBackClick={handleBackClick} currentStep={consumerData?.wizardStep}/>
            </form>
        </section>
    );
}
